import {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {Breadcrumb, ButtonGroup, ButtonToolbar, Col, Dropdown, Row} from 'react-bootstrap';
import Link from 'next/link';
import {getLocalizedRoute, routes} from '@utils/routes';
import {FormattedMessage, useIntl} from 'react-intl';
import {
    localizedListSlugs as offerListSlugs,
    translations as OfferTypeTranslations,
    imperativeTranslations as imperativeOfferTypeTranslations
} from '@constants/OfferType';
import {
    localizedListSlugs as estateListSlugs,
    translations as EstateTypeTranslations,
    imperativeTranslations as imperativeEstateTypeTranslations
} from '@constants/EstateType';
import {AdvertTitle} from '@liveComponents/AdvertTitle';
import {ActionButton} from '@components/ActionButton';
import {Container} from '@components/Container';
import {Disposition, EstateType, OfferType, Scalars} from '.cache/__types__';
import {LoginRequiredAction} from '@liveComponents/Auth';
import {useSession} from 'next-auth/react';
import {useFavoriteLazyQuery} from '@gql/query/advert/favorite.gql';
import {useSetFavoriteMutation} from '@gql/mutation/advert/setFavorite.gql';
import Favorite from '@liveComponents/LoginRequiredInitialPage/Favorite';
import {useLocale} from '@utils/locale';
import {useRouter} from "next/router";
import {DomainLocale} from "next/dist/server/config-shared";

export type HeadingProps = {
    advert: {
        id: Scalars['ID'];
        offerType: OfferType;
        estateType: EstateType;
        disposition: Disposition;
        surface: number;
        regionTree: {
            id: Scalars['ID'];
            name: string;
            uri: string;
        }[];
    };
    isRk?: boolean;
};

export const Heading: FC<HeadingProps> = ({advert, isRk}) => {
    const locale = useLocale();
    const {status} = useSession();
    const intl = useIntl();
    const router = useRouter();
    const [getFavorite, {data: favoriteData}] = useFavoriteLazyQuery();
    const [setFavorite] = useSetFavoriteMutation();
    const [selfUrl, setSelfUrl] = useState('');
    const [copyStatus, setCopyStatus] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (status === 'authenticated') {
            getFavorite({
                variables: {
                    advertId: advert.id,
                },
            });
        }
    }, [advert.id, getFavorite, status]);

    const toggleFavorite = useCallback(() => {
        setFavorite({
            variables: {
                advertId: advert.id,
                state: favoriteData ? !favoriteData.advert?.advertUserstate?.state : true,
            },
        });
    }, [setFavorite, advert.id, favoriteData]);

    const copyToClipboard = useCallback(
        (event) => {
            event.stopPropagation(); // To prevent closing of dropdown
            // @ts-ignore
            navigator.permissions.query({name: 'clipboard-write'}).then((result) => {
                if (result.state == 'granted' || result.state == 'prompt') {
                    navigator.clipboard.writeText(selfUrl).then(
                        function () {
                            setCopyStatus(true);
                        },
                        function () {
                            setCopyStatus(false);
                        },
                    );
                }
            });
        },
        [selfUrl, setCopyStatus],
    );

    useEffect(() => {
        setSelfUrl(window.location.href);
    }, []);

    const breadcrumbs = useMemo(() => {
        let i = 3;
        const dl = router.domainLocales?.find(dl => dl.defaultLocale === router.locale) as DomainLocale;
        const prefix = (dl.http ? 'http://' : 'https://') + dl.domain + router.basePath;
        const breadcrumbs = {
            '@context': 'https://schema.org',
            "@type": "BreadcrumbList",
            "itemListElement": [{
                "@type": "ListItem",
                "position": 1,
                "name": intl.formatMessage({defaultMessage: "Domů"}),
                "item": prefix + getLocalizedRoute(routes.homepage, locale),
            }, {
                "@type": "ListItem",
                "position": 2,
                "name": intl.formatMessage({defaultMessage: 'Výpis nemovitostí'}),
                "item": prefix + getLocalizedRoute(routes.list, locale, {list: ''}),
            }],
        };

        if (advert.offerType !== OfferType.Undefined) {
            breadcrumbs.itemListElement.push({
                "@type": "ListItem",
                "position": i++,
                "name": imperativeOfferTypeTranslations[advert.offerType](intl),
                "item": prefix + getLocalizedRoute(routes.list, locale, {list: [offerListSlugs[locale][advert.offerType]].join('/')}),
            });
        }

        if (advert.offerType !== OfferType.Undefined && advert.estateType !== EstateType.Undefined) {
            breadcrumbs.itemListElement.push({
                "@type": "ListItem",
                "position": i++,
                "name": imperativeEstateTypeTranslations[advert.estateType](intl),
                "item": prefix + getLocalizedRoute(routes.list, locale, {list: [offerListSlugs[locale][advert.offerType], estateListSlugs[locale][advert.estateType]].join('/')}),
            });
        }

        if (advert.offerType !== OfferType.Undefined && advert.estateType !== EstateType.Undefined &&
            advert.regionTree.length > 0) {
            breadcrumbs.itemListElement.push({
                "@type": "ListItem",
                "position": i++,
                "name": advert.regionTree[0].name,
                "item": prefix + getLocalizedRoute(routes.list, locale, {list: [offerListSlugs[locale][advert.offerType], estateListSlugs[locale][advert.estateType], advert.regionTree[0].uri].join('/')}),
            });
        }

        if (advert.offerType !== OfferType.Undefined && advert.estateType !== EstateType.Undefined &&
            advert.regionTree.length > 1) {
            breadcrumbs.itemListElement.push({
                "@type": "ListItem",
                "position": i++,
                "name": advert.regionTree[1].name,
                "item": prefix + getLocalizedRoute(routes.list, locale, {list: [offerListSlugs[locale][advert.offerType], estateListSlugs[locale][advert.estateType], advert.regionTree[0].uri, advert.regionTree[1].uri].join('/')}),
            });
        }

        if (advert.offerType !== OfferType.Undefined && advert.estateType !== EstateType.Undefined &&
            advert.regionTree.length > 2) {
            breadcrumbs.itemListElement.push({
                "@type": "ListItem",
                "position": i++,
                "name": advert.regionTree[2].name,
                "item": prefix + getLocalizedRoute(routes.list, locale, {list: [offerListSlugs[locale][advert.offerType], estateListSlugs[locale][advert.estateType], advert.regionTree[0].uri, advert.regionTree[1].uri, advert.regionTree[2].uri].join('/')}),
            });
        }

        return breadcrumbs;
    }, [advert, intl, locale, router]);

    return (
        <Container variant="narrow">
            <script id={"breadcrumbs"} type="application/ld+json" /*strategy={"beforeInteractive"}*/ dangerouslySetInnerHTML={{__html: JSON.stringify(breadcrumbs)}} />

            <Row className="justify-content-between align-items-center flex-nowrap mb-4">
                <Col>
                    <Breadcrumb>
                        <Link href={getLocalizedRoute(routes.homepage, locale)} passHref>
                            <Breadcrumb.Item className="d-none d-md-inline-block">
                                <FormattedMessage defaultMessage={'Domů'}/>
                            </Breadcrumb.Item>
                        </Link>
                        <Link href={getLocalizedRoute(routes.list, locale, {list: ''})} passHref>
                            <Breadcrumb.Item>
                                <FormattedMessage defaultMessage={'Výpis nemovitostí'}/>
                            </Breadcrumb.Item>
                        </Link>
                        {advert.offerType !== OfferType.Undefined && (
                            <Link
                                href={getLocalizedRoute(routes.list, locale, {list: [offerListSlugs[locale][advert.offerType]].join('/')})}
                                passHref
                            >
                                <Breadcrumb.Item>{OfferTypeTranslations[advert.offerType]}</Breadcrumb.Item>
                            </Link>
                        )}

                        {advert.offerType !== OfferType.Undefined && advert.estateType !== EstateType.Undefined && (
                            <Link
                                href={getLocalizedRoute(routes.list, locale, {
                                    list: [offerListSlugs[locale][advert.offerType], estateListSlugs[locale][advert.estateType]].join('/'),
                                })}
                                passHref
                            >
                                <Breadcrumb.Item>{EstateTypeTranslations[advert.estateType]}</Breadcrumb.Item>
                            </Link>
                        )}

                        {advert.offerType !== OfferType.Undefined &&
                            advert.estateType !== EstateType.Undefined &&
                            advert.regionTree.length > 0 && (
                                <Link
                                    href={getLocalizedRoute(routes.list, locale, {
                                        list: [
                                            offerListSlugs[locale][advert.offerType],
                                            estateListSlugs[locale][advert.estateType],
                                            advert.regionTree[0].uri,
                                        ].join('/'),
                                    })}
                                    passHref
                                >
                                    <Breadcrumb.Item>{advert.regionTree[0].name}</Breadcrumb.Item>
                                </Link>
                            )}

                        {advert.offerType !== OfferType.Undefined &&
                            advert.estateType !== EstateType.Undefined &&
                            advert.regionTree.length > 1 && (
                                <Link
                                    href={getLocalizedRoute(routes.list, locale, {
                                        list: [
                                            offerListSlugs[locale][advert.offerType],
                                            estateListSlugs[locale][advert.estateType],
                                            advert.regionTree[0].uri,
                                            advert.regionTree[1].uri,
                                        ].join('/'),
                                    })}
                                    passHref
                                >
                                    <Breadcrumb.Item>{advert.regionTree[1].name}</Breadcrumb.Item>
                                </Link>
                            )}

                        {advert.offerType !== OfferType.Undefined &&
                            advert.estateType !== EstateType.Undefined &&
                            advert.regionTree.length > 2 && (
                                <Link
                                    href={getLocalizedRoute(routes.list, locale, {
                                        list: [
                                            offerListSlugs[locale][advert.offerType],
                                            estateListSlugs[locale][advert.estateType],
                                            advert.regionTree[0].uri,
                                            advert.regionTree[1].uri,
                                            advert.regionTree[2].uri,
                                        ].join('/'),
                                    })}
                                    passHref
                                >
                                    <Breadcrumb.Item>{advert.regionTree[2].name}</Breadcrumb.Item>
                                </Link>
                            )}
                        <Breadcrumb.Item active>
                            <AdvertTitle
                                offerType={advert.offerType}
                                estateType={advert.estateType}
                                disposition={advert.disposition}
                                surface={advert?.surface}
                                isRk={isRk}
                            />
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
                <Col xs="auto">
                    <ButtonToolbar className="ms-n3 ms-md-n5">
                        <ButtonGroup className="ms-3 ms-md-5">
                            <Dropdown>
                                <Dropdown.Toggle as={ActionButton} iconName="Share">
                                    <FormattedMessage defaultMessage={'Sdílet'}/>
                                </Dropdown.Toggle>
                                <Dropdown.Menu className={'position-absolute minw-0'}>
                                    <Dropdown.Item
                                        href={'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(selfUrl)}>
                                        <ActionButton iconName={'Facebook'}>
                                            <FormattedMessage defaultMessage={'Sdílet na Facebooku'}/>
                                        </ActionButton>
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        href={'https://twitter.com/intent/tweet?url=' + encodeURIComponent(selfUrl)}>
                                        <ActionButton iconName={'Twitter'}>
                                            <FormattedMessage defaultMessage={'Sdílet na Twitteru'}/>
                                        </ActionButton>
                                    </Dropdown.Item>
                                    <Dropdown.Item href={'mailto:?body=' + encodeURIComponent(selfUrl)}>
                                        <ActionButton iconName={'Envelope'}>
                                            <FormattedMessage defaultMessage="Poslat e-mailem"/>
                                        </ActionButton>
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={copyToClipboard}>
                                        <ActionButton iconName={'Copy'}>
                                            {copyStatus === undefined &&
                                                <FormattedMessage defaultMessage={'Kopírovat'}/>}

                                            {copyStatus === true && <FormattedMessage defaultMessage={'Zkopírováno'}/>}

                                            {copyStatus === false &&
                                                <FormattedMessage defaultMessage={'Akce se nezdařila'}/>}
                                        </ActionButton>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </ButtonGroup>
                        <ButtonGroup className="ms-3 ms-md-5">
                            <LoginRequiredAction initialPage={Favorite}>
                                <ActionButton
                                    iconName={favoriteData?.advert?.advertUserstate?.state ? 'HeartFilled' : 'Heart'}
                                    onClick={toggleFavorite}
                                >
                                    {favoriteData?.advert?.advertUserstate?.state ? (
                                        <FormattedMessage defaultMessage={'Uloženo'}/>
                                    ) : (
                                        <FormattedMessage defaultMessage={'Do oblíbených'}/>
                                    )}
                                </ActionButton>
                            </LoginRequiredAction>
                        </ButtonGroup>
                    </ButtonToolbar>
                </Col>
            </Row>
        </Container>
    );
};
